/*!
=========================================================
* AreaTrek Admin Dashboard - Executive Overview
=========================================================
*/

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Chart from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

import {
  Card,
  CardHeader,
  CardBody,
  Progress,
  Container,
  Row,
  Col,
  Button,
  Badge,
} from "reactstrap";

import {
  FiUsers,
  FiCompass,
  FiShield,
  FiCalendar,
  FiActivity,
  FiRefreshCw,
  FiPackage,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiAward,
  FiTrendingUp,
  FiXCircle,
} from "react-icons/fi";

import { chartOptions, parseOptions } from "variables/charts.js";
import Header from "components/Headers/Header.js";
import "../assets/css/dashboard-redesign.css";

import {
  getDashboardStats,
  getDashboardStatsmonthly,
} from "../api/ApiServices";

const Index = () => {
  const navigate = useNavigate();

  // =========================================================
  // STATE
  // =========================================================
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [messageVolumeData, setMessageVolumeData] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalTravelers: 0,
    totalGuides: 0,
    pendingVerification: 0,
    approvedVerification: 0,
    rejectedVerification: 0,
    totalBookings: 0,
    upcomingTours: 0,
    completedTours: 0,
    pendingTours: 0,
    acceptedTours: 0,
    cancelledTours: 0,
    totalPackages: 0,
    totalMemberships: 0,
    totalRevenue: 0,
  });

  if (window.Chart) {
    parseOptions(Chart, chartOptions());
  }

  // =========================================================
  // FETCH DASHBOARD DATA
  // =========================================================
  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      // 1. Fetch Monthly Data
      const monthlyRes = await getDashboardStatsmonthly();
      if (monthlyRes?.success) {
        const data = monthlyRes?.data || {};
        const monthlyStats = Array.isArray(data?.monthlyStats)
          ? data.monthlyStats
          : [];

        const chartData = monthlyStats.map((item) => ({
          label: item?.month || "",
          travelers: Number(item?.travelers || 0),
          guides: Number(item?.guides || 0),
        }));

        setMessageVolumeData(chartData);
      }

      // 2. Fetch Main Stats
      const statsRes = await getDashboardStats();
      if (statsRes?.success) {
        const data = statsRes?.data || {};
        setDashboardStats({
          totalTravelers: Number(data?.totalTravelers || 0),
          totalGuides: Number(data?.totalGuides || 0),
          pendingVerification: Number(data?.pendingVerification || 0),
          approvedVerification: Number(data?.approvedVerification || 0),
          rejectedVerification: Number(data?.rejectedVerification || 0),
          totalBookings: Number(data?.totalBookings || 0),
          upcomingTours: Number(data?.upcomingTours || 0),
          completedTours: Number(data?.completedTours || 0),
          pendingTours: Number(data?.pendingTours || 0),
          acceptedTours: Number(data?.acceptedTours || 0),
          cancelledTours: Number(data?.cancelledTours || 0),
          totalPackages: Number(data?.totalPackages || 0),
          totalMemberships: Number(data?.totalMemberships || 0),
          totalRevenue: Number(data?.totalRevenue || 0),
        });
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // =========================================================
  // COMPUTED METRICS
  // =========================================================
  const monthlyTotalTravelers = messageVolumeData.reduce(
    (total, item) => total + Number(item.travelers || 0),
    0
  );

  const monthlyTotalGuides = messageVolumeData.reduce(
    (total, item) => total + Number(item.guides || 0),
    0
  );

  const peakCombinedCount =
    messageVolumeData.length > 0
      ? Math.max(
          ...messageVolumeData.map(
            (item) =>
              Number(item.travelers || 0) + Number(item.guides || 0)
          )
        )
      : 0;

  const peakCombinedIndex =
    messageVolumeData.length > 0
      ? messageVolumeData.findIndex(
          (item) =>
            Number(item.travelers || 0) + Number(item.guides || 0) ===
            peakCombinedCount
        )
      : -1;

  const peakCombinedMonth =
    peakCombinedIndex >= 0 && messageVolumeData[peakCombinedIndex]
      ? messageVolumeData[peakCombinedIndex].label
      : "—";

  const currentMonthIndex = new Date().getMonth();
  const currentMonthCount =
    messageVolumeData.length > currentMonthIndex
      ? Number(messageVolumeData[currentMonthIndex]?.travelers || 0) +
        Number(messageVolumeData[currentMonthIndex]?.guides || 0)
      : 0;

  const prevMonthIndex = currentMonthIndex > 0 ? currentMonthIndex - 1 : 11;
  const prevMonthCount =
    messageVolumeData.length > prevMonthIndex
      ? Number(messageVolumeData[prevMonthIndex]?.travelers || 0) +
        Number(messageVolumeData[prevMonthIndex]?.guides || 0)
      : 0;

  let computedGrowthRate = 0;
  if (prevMonthCount > 0) {
    computedGrowthRate = Math.round(
      ((currentMonthCount - prevMonthCount) / prevMonthCount) * 100
    );
  } else if (currentMonthCount > 0) {
    computedGrowthRate = 100;
  }

  // Guide Verification stats
  const totalVerifSubmissions =
    dashboardStats.pendingVerification +
    dashboardStats.approvedVerification +
    dashboardStats.rejectedVerification;

  const verifApprovalRate =
    totalVerifSubmissions > 0
      ? Math.round(
          (dashboardStats.approvedVerification / totalVerifSubmissions) * 100
        )
      : dashboardStats.totalGuides > 0 && dashboardStats.pendingVerification === 0
      ? 100
      : 0;

  // Booking completion rate
  const bookingCompletionRate =
    dashboardStats.totalBookings > 0
      ? Math.round(
          (dashboardStats.completedTours / dashboardStats.totalBookings) * 100
        )
      : 0;

  // Has Chart Data?
  const hasChartData =
    messageVolumeData.length > 0 &&
    (monthlyTotalTravelers > 0 || monthlyTotalGuides > 0);

  // =========================================================
  // MONTHLY BAR CHART CONFIGURATION
  // =========================================================
  const darkBarChartData = {
    labels: messageVolumeData.map((item) => item.label),
    datasets: [
      {
        label: "Travelers",
        data: messageVolumeData.map((item) => Number(item.travelers || 0)),
        backgroundColor: "#0356BB",
        hoverBackgroundColor: "#003D80",
        borderRadius: 6,
        borderSkipped: false,
        barPercentage: 0.45,
        categoryPercentage: 0.8,
      },
      {
        label: "Guides",
        data: messageVolumeData.map((item) => Number(item.guides || 0)),
        backgroundColor: "#22C55E",
        hoverBackgroundColor: "#16A34A",
        borderRadius: 6,
        borderSkipped: false,
        barPercentage: 0.45,
        categoryPercentage: 0.8,
      },
    ],
  };

  const darkBarChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    legend: {
      display: true,
      position: "top",
      labels: {
        fontColor: "#64748B",
        fontSize: 12,
        padding: 20,
        usePointStyle: true,
      },
    },
    tooltips: {
      backgroundColor: "#FFFFFF",
      titleFontColor: "#1E293B",
      bodyFontColor: "#64748B",
      borderColor: "#E2E8F0",
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
      xPadding: 12,
      yPadding: 10,
      callbacks: {
        title: function (tooltipItems) {
          if (tooltipItems && tooltipItems.length > 0) {
            return tooltipItems[0].label;
          }
          return "";
        },
        label: function (tooltipItem, data) {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const value = Number(tooltipItem.yLabel || 0);
          return `${dataset.label}: ${value.toLocaleString()}`;
        },
      },
    },
    scales: {
      yAxes: [
        {
          beginAtZero: true,
          gridLines: {
            color: "rgba(226, 232, 240, 0.75)",
            drawBorder: false,
            borderDash: [4, 4],
          },
          ticks: {
            beginAtZero: true,
            fontColor: "#64748B",
            fontSize: 11,
            padding: 8,
            precision: 0,
            callback: function (value) {
              if (value >= 1000) {
                return `${value / 1000}k`;
              }
              return value;
            },
          },
        },
      ],
      xAxes: [
        {
          gridLines: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            fontColor: "#64748B",
            fontSize: 11,
            padding: 8,
            autoSkip: true,
            maxTicksLimit: 12,
          },
        },
      ],
    },
    hover: {
      mode: "index",
      intersect: false,
    },
    animation: {
      duration: 800,
      easing: "easeOutQuart",
    },
  };

  // =========================================================
  // DOUGHNUT CHART - VERIFICATION PIPELINE
  // =========================================================
  const verificationDoughnutData = {
    labels: ["Approved", "Pending Review", "Rejected"],
    datasets: [
      {
        data: [
          dashboardStats.approvedVerification ||
            Math.max(
              0,
              dashboardStats.totalGuides - dashboardStats.pendingVerification
            ),
          dashboardStats.pendingVerification || 0,
          dashboardStats.rejectedVerification || 0,
        ],
        backgroundColor: ["#22C55E", "#F59E0B", "#EF4444"],
        hoverBackgroundColor: ["#16A34A", "#D97706", "#DC2626"],
        borderWidth: 2,
        borderColor: "#FFFFFF",
      },
    ],
  };

  const doughnutOptions = {
    maintainAspectRatio: false,
    responsive: true,
    cutoutPercentage: 70,
    legend: {
      display: false,
    },
    tooltips: {
      backgroundColor: "#FFFFFF",
      titleFontColor: "#1E293B",
      bodyFontColor: "#64748B",
      borderColor: "#E2E8F0",
      borderWidth: 1,
      cornerRadius: 8,
      xPadding: 10,
      yPadding: 8,
    },
  };

  // =========================================================
  // DOUGHNUT CHART - BOOKINGS DISTRIBUTION
  // =========================================================
  const bookingDoughnutData = {
    labels: ["Completed", "Upcoming", "Accepted", "Pending", "Cancelled"],
    datasets: [
      {
        data: [
          dashboardStats.completedTours || 0,
          dashboardStats.upcomingTours || 0,
          dashboardStats.acceptedTours || 0,
          dashboardStats.pendingTours || 0,
          dashboardStats.cancelledTours || 0,
        ],
        backgroundColor: [
          "#22C55E",
          "#0356BB",
          "#0EA5E9",
          "#F59E0B",
          "#EF4444",
        ],
        hoverBackgroundColor: [
          "#16A34A",
          "#003D80",
          "#0284C7",
          "#D97706",
          "#DC2626",
        ],
        borderWidth: 2,
        borderColor: "#FFFFFF",
      },
    ],
  };

  return (
    <>
      <Header />

      <Container className="mt--7 pb-5" fluid>
        {/* =========================================================
            ROW 1: MONTHLY GROWTH CHART + VERIFICATION PIPELINE
        ========================================================= */}
        <Row className="mb-4">
          {/* -------------------------------------------------------
              MONTHLY GROWTH & ACTIVITY CHART
          ------------------------------------------------------- */}
          <Col xl="8" lg="12" className="mb-4 mb-xl-0">
            <Card
              className="border-0 shadow-sm h-100"
              style={{
                background: "#FFFFFF",
                borderRadius: "16px",
                border: "1px solid var(--new-border-light, #E2E8F0)",
              }}
            >
              {loading ? (
                <div className="text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                  <p className="mt-2 text-muted">Loading overview details...</p>
                </div>
              ) : (
                <>
                  <CardHeader
                    className="bg-transparent border-0 d-flex align-items-center justify-content-between flex-wrap"
                    style={{ padding: "24px 24px 12px" }}
                  >
                    <div>
                      <div
                        className="text-uppercase mb-1"
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          letterSpacing: "0.8px",
                          color: "var(--new-text-muted, #64748B)",
                        }}
                      >
                        Platform Growth
                      </div>
                      <h2
                        className="mb-1"
                        style={{
                          color: "var(--new-text-primary, #1E293B)",
                          fontSize: "20px",
                          fontWeight: 700,
                        }}
                      >
                        Monthly Travelers & Guides Activity
                      </h2>
                      <p
                        className="mb-0 text-muted"
                        style={{ fontSize: "13px" }}
                      >
                        Annual onboarding and engagement trajectory
                      </p>
                    </div>

                    <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0">
                      <Button
                        size="sm"
                        color="light"
                        className="border mr-2"
                        disabled={refreshing}
                        onClick={() => fetchDashboardData(true)}
                        style={{
                          borderRadius: "8px",
                          fontWeight: 600,
                          fontSize: "12px",
                          color: "var(--new-text-primary, #1E293B)",
                        }}
                      >
                        <FiRefreshCw
                          className={`mr-1 ${refreshing ? "fa-spin" : ""}`}
                        />
                        Refresh
                      </Button>
                      <span
                        style={{
                          background: "var(--background-blue-light, #EAF3FF)",
                          color: "var(--button-primary, #0356BB)",
                          borderRadius: "8px",
                          padding: "6px 12px",
                          fontSize: "12px",
                          fontWeight: 700,
                        }}
                      >
                        {new Date().getFullYear()}
                      </span>
                    </div>
                  </CardHeader>

                  <CardBody style={{ padding: "10px 24px 24px" }}>
                    {/* Summary Quick Badges */}
                    <div
                      className="d-flex align-items-center mb-3 pb-3 flex-wrap"
                      style={{
                        gap: "20px",
                        borderBottom:
                          "1px solid var(--new-border-light, #E2E8F0)",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            color: "var(--new-text-muted, #64748B)",
                            fontSize: "11px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                          }}
                        >
                          Total Travelers
                        </div>
                        <div
                          style={{
                            color: "var(--button-primary, #0356BB)",
                            fontSize: "18px",
                            fontWeight: 700,
                          }}
                        >
                          {Number(
                            dashboardStats.totalTravelers ||
                              monthlyTotalTravelers
                          ).toLocaleString()}
                        </div>
                      </div>

                      <div
                        style={{
                          width: "1px",
                          height: "32px",
                          background: "var(--new-border-light, #E2E8F0)",
                        }}
                      />

                      <div>
                        <div
                          style={{
                            color: "var(--new-text-muted, #64748B)",
                            fontSize: "11px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                          }}
                        >
                          Total Guides
                        </div>
                        <div
                          style={{
                            color: "var(--button-secondary, #22C55E)",
                            fontSize: "18px",
                            fontWeight: 700,
                          }}
                        >
                          {Number(
                            dashboardStats.totalGuides || monthlyTotalGuides
                          ).toLocaleString()}
                        </div>
                      </div>

                      <div
                        style={{
                          width: "1px",
                          height: "32px",
                          background: "var(--new-border-light, #E2E8F0)",
                        }}
                      />

                      <div>
                        <div
                          style={{
                            color: "var(--new-text-muted, #64748B)",
                            fontSize: "11px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                          }}
                        >
                          Peak Activity
                        </div>
                        <div
                          style={{
                            color: "var(--new-text-primary, #1E293B)",
                            fontSize: "18px",
                            fontWeight: 700,
                          }}
                        >
                          {peakCombinedMonth}
                        </div>
                      </div>

                      <div
                        style={{
                          width: "1px",
                          height: "32px",
                          background: "var(--new-border-light, #E2E8F0)",
                        }}
                      />

                      <div>
                        <div
                          style={{
                            color: "var(--new-text-muted, #64748B)",
                            fontSize: "11px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                          }}
                        >
                          Traveler:Guide Ratio
                        </div>
                        <div
                          style={{
                            color: "var(--new-text-primary, #1E293B)",
                            fontSize: "18px",
                            fontWeight: 700,
                          }}
                        >
                          {dashboardStats.totalTravelers > 0
                            ? `${Math.max(
                                1,
                                Math.round(
                                  dashboardStats.totalTravelers /
                                    Math.max(1, dashboardStats.totalGuides)
                                )
                              )} : 1`
                            : "1 : 1"}
                        </div>
                      </div>
                    </div>

                    {/* Chart Area */}
                    <div
                      className="chart"
                      style={{ height: "300px", position: "relative" }}
                    >
                      {hasChartData ? (
                        <Bar
                          data={darkBarChartData}
                          options={darkBarChartOptions}
                        />
                      ) : (
                        <div className="activity-empty-state h-100 d-flex flex-column align-items-center justify-content-center">
                          <div className="activity-empty-icon mb-2">
                            <FiActivity />
                          </div>
                          <h4 className="font-weight-bold text-dark mb-1">
                            No Monthly Activity Yet
                          </h4>
                          <p className="text-muted small mb-0">
                            Monthly registration analytics for{" "}
                            {new Date().getFullYear()} will populate as users
                            join the platform.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Chart Legend Footer */}
                    <div
                      className="d-flex align-items-center justify-content-between mt-3 pt-3 flex-wrap"
                      style={{
                        borderTop:
                          "1px solid var(--new-border-light, #E2E8F0)",
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <span
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            background: "#0356BB",
                            display: "inline-block",
                            marginRight: "6px",
                          }}
                        />
                        <span
                          style={{
                            color: "var(--new-text-muted, #64748B)",
                            fontSize: "12px",
                            marginRight: "16px",
                            fontWeight: 500,
                          }}
                        >
                          Travelers
                        </span>

                        <span
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            background: "#22C55E",
                            display: "inline-block",
                            marginRight: "6px",
                          }}
                        />
                        <span
                          style={{
                            color: "var(--new-text-muted, #64748B)",
                            fontSize: "12px",
                            fontWeight: 500,
                          }}
                        >
                          Guides
                        </span>
                      </div>

                      <div
                        style={{
                          color: "var(--new-text-muted, #64748B)",
                          fontSize: "12px",
                        }}
                      >
                        Peak Combined Volume:{" "}
                        <strong
                          style={{
                            color: "var(--button-primary, #0356BB)",
                            fontWeight: 700,
                          }}
                        >
                          {peakCombinedCount.toLocaleString()}
                        </strong>
                      </div>
                    </div>
                  </CardBody>
                </>
              )}
            </Card>
          </Col>

          {/* -------------------------------------------------------
              GUIDE VERIFICATION & COMPLIANCE HEALTH (DONUT)
          ------------------------------------------------------- */}
          <Col xl="4" lg="12">
            <Card
              className="border-0 shadow-sm h-100"
              style={{
                background: "#FFFFFF",
                borderRadius: "16px",
                border: "1px solid var(--new-border-light, #E2E8F0)",
              }}
            >
              {loading ? (
                <div className="text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                  <p className="mt-2 text-muted">Loading insights...</p>
                </div>
              ) : (
                <>
                  <CardHeader className="bg-transparent border-0 pb-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                    <div>
                      <div
                        className="text-uppercase mb-1"
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          letterSpacing: "0.8px",
                          color: "var(--new-text-muted, #64748B)",
                        }}
                      >
                        Safety & Trust
                      </div>
                      <h3
                        className="mb-0"
                        style={{
                          color: "var(--new-text-primary, #1E293B)",
                          fontSize: "18px",
                          fontWeight: 700,
                        }}
                      >
                        Guide Verification Pipeline
                      </h3>
                    </div>
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: "38px",
                        height: "38px",
                        borderRadius: "10px",
                        background:
                          dashboardStats.pendingVerification > 0
                            ? "rgba(245, 158, 11, 0.12)"
                            : "var(--background-green-light, #F0F9E8)",
                        color:
                          dashboardStats.pendingVerification > 0
                            ? "#F59E0B"
                            : "#22C55E",
                        fontSize: "18px",
                      }}
                    >
                      <FiShield />
                    </div>
                  </CardHeader>

                  <CardBody className="px-4 py-3 d-flex flex-column justify-content-between">
                    {/* Visual Donut Chart Container */}
                    <div
                      style={{
                        height: "170px",
                        position: "relative",
                        margin: "10px 0",
                      }}
                      className="d-flex align-items-center justify-content-center"
                    >
                      <Doughnut
                        data={verificationDoughnutData}
                        options={doughnutOptions}
                      />
                      {/* Center Label in Donut */}
                      <div
                        style={{
                          position: "absolute",
                          textAlign: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "22px",
                            fontWeight: 800,
                            color: "var(--new-text-primary, #1E293B)",
                            lineHeight: 1,
                          }}
                        >
                          {verifApprovalRate}%
                        </div>
                        <div
                          style={{
                            fontSize: "10px",
                            color: "var(--new-text-muted, #64748B)",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            marginTop: "2px",
                          }}
                        >
                          Approval
                        </div>
                      </div>
                    </div>

                    {/* Breakdown Badges Grid */}
                    <div className="d-flex justify-content-between text-center mb-3">
                      <div
                        className="px-3 py-2 flex-fill mr-1"
                        style={{
                          background: "rgba(34, 197, 94, 0.08)",
                          borderRadius: "10px",
                          border: "1px solid rgba(34, 197, 94, 0.2)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: 700,
                            color: "#22C55E",
                          }}
                        >
                          {dashboardStats.approvedVerification ||
                            Math.max(
                              0,
                              dashboardStats.totalGuides -
                                dashboardStats.pendingVerification
                            )}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#166534",
                            fontWeight: 600,
                          }}
                        >
                          Approved
                        </div>
                      </div>

                      <div
                        className="px-3 py-2 flex-fill mx-1"
                        style={{
                          background: "rgba(245, 158, 11, 0.08)",
                          borderRadius: "10px",
                          border: "1px solid rgba(245, 158, 11, 0.2)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: 700,
                            color: "#D97706",
                          }}
                        >
                          {dashboardStats.pendingVerification}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#92400E",
                            fontWeight: 600,
                          }}
                        >
                          Pending
                        </div>
                      </div>

                      <div
                        className="px-3 py-2 flex-fill ml-1"
                        style={{
                          background: "rgba(239, 68, 68, 0.08)",
                          borderRadius: "10px",
                          border: "1px solid rgba(239, 68, 68, 0.2)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: 700,
                            color: "#EF4444",
                          }}
                        >
                          {dashboardStats.rejectedVerification || 0}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#991B1B",
                            fontWeight: 600,
                          }}
                        >
                          Rejected
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar & Status */}
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span
                          style={{
                            fontSize: "12px",
                            color: "var(--new-text-muted, #64748B)",
                            fontWeight: 500,
                          }}
                        >
                          Safety Compliance Level
                        </span>
                        <Badge
                          color={
                            dashboardStats.pendingVerification > 0
                              ? "warning"
                              : "success"
                          }
                          pill
                        >
                          {dashboardStats.pendingVerification > 0
                            ? `${dashboardStats.pendingVerification} Awaiting Action`
                            : "Fully Compliant"}
                        </Badge>
                      </div>
                      <Progress
                        value={verifApprovalRate}
                        max={100}
                        style={{
                          height: "6px",
                          borderRadius: "10px",
                          backgroundColor: "#E2E8F0",
                        }}
                        barStyle={{
                          background:
                            "linear-gradient(90deg, #F59E0B 0%, #22C55E 100%)",
                          borderRadius: "10px",
                        }}
                      />
                    </div>
                  </CardBody>
                </>
              )}
            </Card>
          </Col>
        </Row>

        {/* =========================================================
            ROW 2: 3 BALANCED ANALYTICS CARDS (OPERATIONS & COMMERCIALS)
        ========================================================= */}
        <Row>
          {/* -------------------------------------------------------
              CARD 1: BOOKINGS LIFECYCLE & STATUS BREAKDOWN
          ------------------------------------------------------- */}
          <Col xl="4" lg="6" md="12" className="mb-4">
            <Card
              className="border-0 shadow-sm h-100"
              style={{
                background: "#FFFFFF",
                borderRadius: "16px",
                border: "1px solid var(--new-border-light, #E2E8F0)",
              }}
            >
              <CardHeader className="bg-transparent border-0 pb-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                <div>
                  <div
                    className="text-uppercase mb-1"
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.8px",
                      color: "var(--new-text-muted, #64748B)",
                    }}
                  >
                    Operations
                  </div>
                  <h3
                    className="mb-0"
                    style={{
                      color: "var(--new-text-primary, #1E293B)",
                      fontSize: "18px",
                      fontWeight: 700,
                    }}
                  >
                    Booking Pipeline
                  </h3>
                </div>
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "10px",
                    background: "var(--background-blue-light, #EAF3FF)",
                    color: "var(--button-primary, #0356BB)",
                    fontSize: "18px",
                  }}
                >
                  <FiCalendar />
                </div>
              </CardHeader>

              <CardBody className="px-4 py-3 d-flex flex-column justify-content-between">
                <div>
                  {/* Total Bookings Summary Box */}
                  <div
                    className="p-3 mb-3 d-flex justify-content-between align-items-center"
                    style={{
                      background: "var(--background-light, #F7F9FC)",
                      borderRadius: "12px",
                      border: "1px solid var(--new-border-light, #E2E8F0)",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "var(--new-text-muted, #64748B)",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Total Bookings
                      </div>
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: 800,
                          color: "var(--new-text-primary, #1E293B)",
                        }}
                      >
                        {dashboardStats.totalBookings.toLocaleString()}
                      </div>
                    </div>
                    <Badge color="primary" pill style={{ fontSize: "11px" }}>
                      All-time
                    </Badge>
                  </div>

                  {/* Multi-Status Metrics List */}
                  <div className="d-flex flex-column" style={{ gap: "10px" }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span
                        style={{
                          fontSize: "13px",
                          color: "var(--new-text-muted, #64748B)",
                        }}
                      >
                        <FiCheckCircle className="text-success mr-2" /> Completed
                        Tours
                      </span>
                      <strong style={{ fontSize: "14px", color: "#1E293B" }}>
                        {dashboardStats.completedTours}
                      </strong>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <span
                        style={{
                          fontSize: "13px",
                          color: "var(--new-text-muted, #64748B)",
                        }}
                      >
                        <FiClock className="text-primary mr-2" /> Upcoming Tours
                      </span>
                      <strong style={{ fontSize: "14px", color: "#1E293B" }}>
                        {dashboardStats.upcomingTours}
                      </strong>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <span
                        style={{
                          fontSize: "13px",
                          color: "var(--new-text-muted, #64748B)",
                        }}
                      >
                        <FiActivity className="text-warning mr-2" /> Pending
                        Requests
                      </span>
                      <strong style={{ fontSize: "14px", color: "#1E293B" }}>
                        {dashboardStats.pendingTours}
                      </strong>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <span
                        style={{
                          fontSize: "13px",
                          color: "var(--new-text-muted, #64748B)",
                        }}
                      >
                        <FiXCircle className="text-danger mr-2" /> Cancelled /
                        Rejected
                      </span>
                      <strong style={{ fontSize: "14px", color: "#1E293B" }}>
                        {dashboardStats.cancelledTours}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* Completion Progress Bar */}
                <div
                  className="pt-3 mt-3"
                  style={{
                    borderTop:
                      "1px solid var(--new-border-light, #E2E8F0)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span
                      style={{
                        fontSize: "12px",
                        color: "var(--new-text-muted, #64748B)",
                        fontWeight: 500,
                      }}
                    >
                      Tour Completion Rate
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#22C55E",
                      }}
                    >
                      {bookingCompletionRate}%
                    </span>
                  </div>
                  <Progress
                    value={bookingCompletionRate}
                    max={100}
                    style={{
                      height: "6px",
                      borderRadius: "10px",
                      backgroundColor: "#E2E8F0",
                    }}
                    barStyle={{
                      background: "#22C55E",
                      borderRadius: "10px",
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* -------------------------------------------------------
              CARD 2: COMMERCIAL & INVENTORY OVERVIEW
          ------------------------------------------------------- */}
          <Col xl="4" lg="6" md="12" className="mb-4">
            <Card
              className="border-0 shadow-sm h-100"
              style={{
                background: "#FFFFFF",
                borderRadius: "16px",
                border: "1px solid var(--new-border-light, #E2E8F0)",
              }}
            >
              <CardHeader className="bg-transparent border-0 pb-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                <div>
                  <div
                    className="text-uppercase mb-1"
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.8px",
                      color: "var(--new-text-muted, #64748B)",
                    }}
                  >
                    Inventory & Volume
                  </div>
                  <h3
                    className="mb-0"
                    style={{
                      color: "var(--new-text-primary, #1E293B)",
                      fontSize: "18px",
                      fontWeight: 700,
                    }}
                  >
                    Platform Commercials
                  </h3>
                </div>
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "10px",
                    background: "var(--background-green-light, #F0F9E8)",
                    color: "var(--button-secondary, #22C55E)",
                    fontSize: "18px",
                  }}
                >
                  <FiDollarSign />
                </div>
              </CardHeader>

              <CardBody className="px-4 py-3 d-flex flex-column justify-content-between">
                <div>
                  {/* Gross Volume Box */}
                  <div
                    className="p-3 mb-3 d-flex justify-content-between align-items-center"
                    style={{
                      background: "rgba(34, 197, 94, 0.08)",
                      borderRadius: "12px",
                      border: "1px solid rgba(34, 197, 94, 0.2)",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#166534",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Gross Booking Volume
                      </div>
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: 800,
                          color: "#16A34A",
                        }}
                      >
                        $
                        {Number(
                          dashboardStats.totalRevenue || 0
                        ).toLocaleString()}
                      </div>
                    </div>
                    <Badge color="success" pill style={{ fontSize: "11px" }}>
                      Volume
                    </Badge>
                  </div>

                  {/* Commercial Metrics */}
                  <div className="d-flex flex-column" style={{ gap: "12px" }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span
                        style={{
                          fontSize: "13px",
                          color: "var(--new-text-muted, #64748B)",
                        }}
                      >
                        <FiPackage className="text-primary mr-2" /> Active Tour
                        Packages
                      </span>
                      <strong style={{ fontSize: "14px", color: "#1E293B" }}>
                        {dashboardStats.totalPackages.toLocaleString()} Listed
                      </strong>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <span
                        style={{
                          fontSize: "13px",
                          color: "var(--new-text-muted, #64748B)",
                        }}
                      >
                        <FiAward className="text-warning mr-2" /> Membership
                        Subscriptions
                      </span>
                      <strong style={{ fontSize: "14px", color: "#1E293B" }}>
                        {dashboardStats.totalMemberships} Active
                      </strong>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <span
                        style={{
                          fontSize: "13px",
                          color: "var(--new-text-muted, #64748B)",
                        }}
                      >
                        <FiUsers className="text-info mr-2" /> Total Platform
                        Users
                      </span>
                      <strong style={{ fontSize: "14px", color: "#1E293B" }}>
                        {(
                          dashboardStats.totalTravelers +
                          dashboardStats.totalGuides
                        ).toLocaleString()}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* Average Ticket Insight */}
                <div
                  className="pt-3 mt-3"
                  style={{
                    borderTop:
                      "1px solid var(--new-border-light, #E2E8F0)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span
                      style={{
                        fontSize: "12px",
                        color: "var(--new-text-muted, #64748B)",
                        fontWeight: 500,
                      }}
                    >
                      Avg. Booking Value
                    </span>
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "var(--button-primary, #0356BB)",
                      }}
                    >
                      $
                      {dashboardStats.totalBookings > 0
                        ? Math.round(
                            dashboardStats.totalRevenue /
                              dashboardStats.totalBookings
                          ).toLocaleString()
                        : "0"}
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* -------------------------------------------------------
              CARD 3: PLATFORM PERFORMANCE & SYSTEM HEALTH
          ------------------------------------------------------- */}
          
        </Row>
      </Container>
    </>
  );
};

export default Index;
